import type { ElementType } from "react";

import type { Action } from "kbar";
import { upperFirst } from "lodash";

import type { ResourceInteractionType } from "@hightouch/lib/resource-interactions/resource-interaction-types";

export type CommandResultType =
  | ResourceInteractionType
  | "sources"
  | "destinations"
  | "ads"
  | "library"
  // Native Delivery's own, apart from "campaigns", which are Lifecycle Studio projects.
  | "native-delivery-campaigns"
  | "native-delivery-messages"
  | "projects"
  | "tasks"
  | "chats"
  | "traits"
  | "priority-lists"
  | "metrics";

// Plural nouns as they read mid-sentence ("Search audiences"), keeping product names capitalized.
export const COMMAND_RESULT_TYPE_NOUNS: Record<CommandResultType, string> = {
  audiences: "audiences",
  journeys: "journeys",
  syncs: "syncs",
  models: "models",
  charts: "charts",
  dashboards: "dashboards",
  experiments: "experiments",
  campaigns: "Lifecycle Studio campaigns",
  "ad-studio-projects": "Ad Studio chats",
  reports: "reports",
  sources: "sources",
  destinations: "destinations",
  ads: "ads",
  library: "library",
  "native-delivery-campaigns": "Native Delivery campaigns",
  "native-delivery-messages": "messages",
  projects: "projects",
  tasks: "tasks",
  chats: "chats",
  traits: "traits",
  "priority-lists": "priority lists",
  metrics: "metrics",
};

export const commandResultTypeLabel = (type: CommandResultType) =>
  upperFirst(COMMAND_RESULT_TYPE_NOUNS[type]);

const commandScopeNoun = (scope: Pick<CommandScope, "type" | "noun">) =>
  scope.noun ?? COMMAND_RESULT_TYPE_NOUNS[scope.type];

export const commandScopeLabel = (scope: Pick<CommandScope, "type" | "noun">) =>
  upperFirst(commandScopeNoun(scope));

// Where a query starts one of the label's words, so "camp" offers "Native Delivery campaigns"; -1 when it starts none.
export const scopeLabelMatchStart = (label: string, query: string) =>
  ` ${label.toLowerCase()}`.indexOf(` ${query.toLowerCase()}`);

export const commandScopePlaceholder = (
  scope: Pick<CommandScope, "type" | "noun">,
) => `Search ${commandScopeNoun(scope)}`;

// An object the palette can open. `id` is unique within its `type`.
export type CommandResult = {
  id: string;
  type: CommandResultType;
  name: string;
  icon: ElementType;
  href: string;
  updatedAt?: string;
  // Tells apart rows that share a name (a sync's model, a destination's type), shown only then.
  detail?: string;
  // Opens with the current location as `location.state.from`, so the destination's close returns here.
  keepsOrigin?: boolean;
  // Same-named rows read "Edited <time>" rather than "Updated <time>" past their detail.
  isEditedLabelled?: boolean;
};

export type CommandScope = {
  type: CommandResultType;
  icon: ElementType;
  // Narrows an ads or Ad Studio projects scope to one brand, as the brand-filtered Ads grid that opened it does.
  brandProfileId?: string;
  // The type's own page, which its row opens on Enter; Tab searches inside instead.
  href?: string;
  // Replaces the type's noun where the nav names it differently, as the Library is "Content" with the product tabs.
  noun?: string;
};

// Results one source contributes. `zeroQuery` sources also list before anything is typed.
// `deferred` results arrive late (server search), so they rank only among themselves, below every other row.
// Each of `scopes` gets a "Search <type>" row when the query starts its label.
// A failed source sets `isError`; the palette shows one retry row, which calls `onRetry`, once it stops loading.
export type CommandResultSource = {
  id: string;
  results: CommandResult[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  zeroQuery?: boolean;
  deferred?: boolean;
  scopes?: CommandScope[];
};

export type CommandWorkspaceDetails = {
  id: string;
  memberCount: number | null;
};

type CommandActionExtras = {
  // kbar types `icon` as a rendered node, but rows render a component so they can size and tint it.
  iconComponent?: ElementType;
  // A workspace row shows its subtitle (the organization), member count and id in aligned columns.
  workspace?: CommandWorkspaceDetails;
  // The type Tab searches from this command, for a link to a type's page whose id is not the type.
  searches?: CommandResultType;
};

export type CommandAction = Action & CommandActionExtras;

// kbar copies every field of a registered action onto its ActionImpl, so the extras survive registration.
declare module "kbar/lib/action/ActionImpl" {
  interface ActionImpl extends CommandActionExtras {}
}

export type CommandBarPageObject = Pick<
  CommandResult,
  "id" | "type" | "name" | "icon"
>;

export type CommandBarPageContext = {
  object: CommandBarPageObject;
  actions: Array<{ id: string; icon: ElementType }>;
};
